// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useState } from "react";
import _, { Dictionary } from "lodash";
import { ConfirmDialog, Dialog } from "components/dialog";
import makeStyles from "@material-ui/styles/makeStyles";
import { connect } from "react-redux";
import Button from "components/form/button";
import { IStore } from "reducers";
import { IState as Scope } from "reducers/scope";
import { formatMessage } from "lib/globalize-util";
import {
    customizationSelectionDefaultValueMap,
    IAttribute,
    IBinding,
    ICustomizationTemplate,
    IPlanar2dModel,
    IThreediumCustomization,
    IThreediumModel,
    IVisualizer,
    IVisualizerAttribute,
    VisualizerTemplate,
} from "constants/product-customizations/customization";

import {
    CustomizationSelectionMap,
    TCustomizationSelection,
    TNestedCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";

import theme from "styles/theme";
import classNames from "classnames";
import CircularProgress from "material-ui/CircularProgress";
import { CUSTOMIZATIONS } from "constants/integration/customizations";
import { Customizer3d } from "components/customization-modal/3d-product-customization/customizer3d";
import { Customizer2d } from "components/customization-modal/2d-product-customization/customizer2d";
import { resetUploadedImage } from "../../actions/customization-actions";
import Collapse from "@material-ui/core/Collapse";
import { IThreediumTemplate } from "../../constants/product-customizations/threedium";
import { AttributesDropdown, ATTRIBUTES_DROPDOWN_KEY } from "./attributes-dropdown";
import { deleteAttributeById, reorderSelectedValues, idMapping, getDropdownState, mutateAttributes } from "./helpers";
import { Attribute } from "./attributes/attribute";
import { AttributeDetails } from "./attributes/attribute-details";
import { IProduct } from "../../records/item/item";
import { CustomizationModalProvider } from "./context/customization-modal-context";
import { CustomizationImageView } from "./customization-image-view";
import { CustomizationDescription } from "./attributes/customization-description";

export interface ICustomizationModalStateProps {
    scope: Scope;
    isFetching: boolean;
    error?: string | Error;
}

export interface ICustomizationModalProps {
    open: boolean;
    productName: string;
    product3dModel?: IThreediumModel;
    product2dModel?: IPlanar2dModel;
    readonly?: boolean;
    customizationTemplate?: ICustomizationTemplate;
    attributesValues?: CustomizationSelectionMap;
    defaultAttributesValues?: CustomizationSelectionMap;
    onAttributesChange?: (map: CustomizationSelectionMap) => void;
    onClose: () => void;
    companyMerchantId?: string;
    product: IProduct;
    isProductFetching?: boolean;
    isPDP?: boolean;
    onShowBulkCustomizationModal?: () => void;
}

export interface ICustomizationModalDispatchProps {
    resetUploadedImage?: () => void;
}

const ProductModelVisualizerKey = "product_model";
type VisualizationDependency = {
    visualizer: IVisualizer;
    binding: IBinding;
    visualizerAttribute: IVisualizerAttribute;
};

export const CustomizationModalContainer: React.FC<
    ICustomizationModalProps & ICustomizationModalStateProps & ICustomizationModalDispatchProps
> = ({
    open,
    productName,
    product3dModel,
    product2dModel,
    readonly = false,
    attributesValues,
    onAttributesChange,
    onClose,
    scope,
    customizationTemplate,
    error,
    isFetching,
    resetUploadedImage,
    companyMerchantId = "",
    defaultAttributesValues,
    product,
    isProductFetching,
    onShowBulkCustomizationModal,
    isPDP,
}) => {
    const classes = useStyles();
    const [attributesToShow, setAttributesToShow] = useState<IAttribute[]>([]);
    const [selectedAttribute, setSelectedAttribute] = useState<IAttribute>(null);
    const [selectedValues, setSelectedValues] = useState(new CustomizationSelectionMap());
    const [dirty, setDirty] = useState(false);
    const [dirtyDialogOpen, setDirtyDialogOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState(new Map<string, string>());
    const [activeVisualizers, setActiveVisualizers] = useState(new Set<string>());
    const [brokenModels, setBrokenModels] = useState<Dictionary<boolean>>({});
    const [selectedOptionalAttributes, setSelectedOptionalAttributes] = useState<IAttribute[]>([]);
    const [showAttribute, setShowAttribute] = useState(true);
    const [invalidRequiredAttributes, setInvalidRequiredAttributes] = useState(new Map<string, IAttribute>());
    const { attributes, attributeLimit, description: templateDescription } = customizationTemplate || {};
    const visualizers = customizationTemplate?.visualizers as VisualizerTemplate[];

    const allowBulkApplyCustomizations = !!scope.brand?.hasPreference("allow_product_customization_bulk_apply");

    const { requiredAttributes, optionalAttributes, optionalAttributeLimit } = useMemo(
        () => getDropdownState(attributes, attributeLimit),
        [attributes, attributeLimit],
    );

    const orderedAttributes = useMemo(
        () => requiredAttributes.concat(optionalAttributes),
        [requiredAttributes, optionalAttributes],
    );

    const hasValidationErrors =
        !!validationErrors.size || attributesToShow.length < (attributeLimit?.min ?? Number.MIN_SAFE_INTEGER);

    useEffect(() => {
        setSelectedValues(open ? attributesValues : new CustomizationSelectionMap());
    }, [open, attributesValues]);

    useEffect(() => {
        if (!optionalAttributeLimit) {
            setAttributesToShow(mutateAttributes(requiredAttributes));
            return;
        }

        const selectedOptionalAttributes = optionalAttributes.filter(({ _id: id }) => selectedValues?.has(id));
        setAttributesToShow(mutateAttributes(requiredAttributes.concat(selectedOptionalAttributes)));
        setSelectedOptionalAttributes(mutateAttributes(selectedOptionalAttributes));
    }, [optionalAttributeLimit, selectedValues, requiredAttributes, optionalAttributes]);

    const setAttributeValidationError = (selectedAttributes: CustomizationSelectionMap): void => {
        if (!attributeLimit || selectedAttributes.size >= attributeLimit.min) {
            return;
        }

        handleAttributeValidationError(
            ATTRIBUTES_DROPDOWN_KEY,
            formatMessage("At least |count| attributes should be selected", { count: optionalAttributeLimit.min }),
        );
    };

    const visualizationDependencies: Dictionary<VisualizationDependency> = useMemo(() => {
        if (!attributesToShow?.length || !visualizers?.length) {
            return {};
        }

        type Bindable = { _id: string; binding?: IBinding };
        // including nested selectable bindings to `visualizationDependencies` map
        const nestedBindables: Bindable[] = attributesToShow
            .flatMap((attribute) => attribute.childAttributes ?? [])
            .flatMap((childAttribute) => childAttribute.metadata?.selectable?.values ?? [])
            .filter((value) => value.binding);

        const bindables: Bindable[] = (attributesToShow as Bindable[]).concat(nestedBindables);

        const visualizersMap = idMapping(visualizers);
        visualizers.forEach((visualizer) => {
            visualizer.attributesMap = idMapping(visualizer.attributes);
        });

        return bindables.reduce((map, { _id: id, binding }) => {
            const visualizer = visualizersMap[binding?.visualizerId];
            const visualizerAttribute = visualizer?.attributesMap[binding?.destinationId];
            if (visualizer && visualizerAttribute) {
                map[id] = { visualizer, binding, visualizerAttribute };
            }

            return map;
        }, {} as Dictionary<VisualizationDependency>);
    }, [attributesToShow, visualizers]);

    const defaultValues = useMemo(
        () => attributesToShow && customizationSelectionDefaultValueMap(attributesToShow),
        [attributesToShow],
    );

    const {
        visualizerAttribute: selectedVisualizerAttribute,
        visualizer: selectedVisualizer,
        binding: selectedBinding,
    } = visualizationDependencies?.[selectedAttribute?._id] ?? ({} as Partial<VisualizationDependency>);

    const { threedium: threediumModelAttribute } = selectedVisualizerAttribute ?? ({} as Partial<IVisualizerAttribute>);

    useEffect(() => {
        const id = selectedVisualizer?._id;
        if (!id || activeVisualizers.has(id)) {
            return;
        }

        setActiveVisualizers((previous) => new Set(!selectedAttribute ? [] : [...previous, id]));
    }, [selectedVisualizer?._id, selectedAttribute, activeVisualizers]);

    useEffect(() => {
        if (!attributesToShow?.length) {
            setSelectedAttribute(null);
            return;
        }

        if (selectedAttribute?.printed?.image) {
            resetUploadedImage();
        }

        if (!attributesToShow.find((at) => at._id === selectedAttribute?._id)) {
            const attribute = attributesToShow.find(({ _id: id }) => selectedValues.has(id));
            setSelectedAttribute(attribute ?? attributesToShow[0]);
        }
    }, [attributesToShow, selectedAttribute, setSelectedAttribute, resetUploadedImage, selectedValues]);

    const handleAttributeClick = useCallback(
        (e) => {
            const index = e.currentTarget.getAttribute("data-key");
            const clickedAttribute = attributesToShow[index];
            if (!clickedAttribute) {
                return;
            }

            setSelectedAttribute(clickedAttribute);
            if (selectedAttribute === clickedAttribute) {
                setShowAttribute((showAttribute) => !showAttribute);
            } else {
                setShowAttribute(true);
            }
        },
        [attributesToShow, setSelectedAttribute, selectedAttribute],
    );

    const validateRequiredAttribute = useCallback(
        (customization: TCustomizationSelection, attribute: IAttribute) => {
            const { _id, isRequired } = attribute;
            const shouldRemove = isRequired && customization?.value && invalidRequiredAttributes.has(_id);
            if (!shouldRemove) {
                return;
            }

            setInvalidRequiredAttributes((attributesMap) => {
                if (!attributesMap.get(_id)) {
                    return attributesMap;
                }

                const newMap = new Map(attributesMap);
                newMap.delete(_id);

                return newMap;
            });
        },
        [setInvalidRequiredAttributes, invalidRequiredAttributes],
    );

    const handleAttributeChange = useCallback(
        (value: TCustomizationSelection): void => {
            const id = selectedAttribute._id;

            const values = new CustomizationSelectionMap(selectedValues);
            values.set(id, value);
            validateRequiredAttribute(value, selectedAttribute);

            setDirty(true);
            setSelectedValues(values);
        },
        [selectedValues, selectedAttribute, validateRequiredAttribute],
    );

    const handleAttributeValidationError = useCallback((id: string, error: string | null): void => {
        setValidationErrors((validationErrors) => {
            const currentError = validationErrors.get(id) ?? null;
            if (currentError === error) {
                return validationErrors;
            }

            const newValidationErrors = new Map(validationErrors);
            if (!error) {
                newValidationErrors.delete(id);
            } else {
                newValidationErrors.set(id, error);
            }

            return newValidationErrors;
        });
    }, []);

    const updatedInvalidAttributes = useCallback(() => {
        const invalidAttributes = attributesToShow.reduce((acc, attribute) => {
            const { isRequired, _id } = attribute;
            if (isRequired && !selectedValues.get(_id)?.value) {
                acc.set(_id, attribute);
            }

            return acc;
        }, new Map<string, IAttribute>());

        setInvalidRequiredAttributes(invalidAttributes);
        return invalidAttributes;
    }, [attributesToShow, setInvalidRequiredAttributes, selectedValues]);

    const handleForceClose = useCallback(() => {
        setSelectedValues(_.cloneDeep(attributesValues));
        onClose();
        setDirty(false);
        setDirtyDialogOpen(false);
        setInvalidRequiredAttributes(new Map());
    }, [onClose, attributesValues]);

    const handleSave = useCallback(() => {
        if (updatedInvalidAttributes()?.size > 0) {
            return;
        }

        onAttributesChange?.(reorderSelectedValues(orderedAttributes, selectedValues));
        handleForceClose();
    }, [onAttributesChange, selectedValues, handleForceClose, orderedAttributes, updatedInvalidAttributes]);

    const handleClose = useCallback(() => {
        if (dirty && !readonly) {
            setDirtyDialogOpen(true);
        } else {
            handleForceClose();
        }
    }, [dirty, readonly, handleForceClose, setDirtyDialogOpen]);

    const handleDirtyDialogClose = useCallback(() => {
        setDirtyDialogOpen(false);
    }, []);

    const handleOpenBulkModal = useCallback(() => {
        handleSave();
        onShowBulkCustomizationModal();
    }, [handleSave, onShowBulkCustomizationModal]);

    const getVisualizerImage = (templateAttribute: IAttribute, visualizers: IVisualizer[]): string | undefined => {
        const { visualizerId, destinationId } = templateAttribute?.binding ?? {};
        if (!visualizerId || !destinationId) {
            return;
        }

        return visualizers
            ?.find((visualizer) => visualizer._id === visualizerId)
            ?.attributes.find((visualizerAttribute) => visualizerAttribute._id === destinationId)?.image?.url;
    };

    const selectedValue = useMemo<TCustomizationSelection>(
        () => (selectedAttribute && selectedValues ? _.cloneDeep(selectedValues.get(selectedAttribute._id)) : null),
        [selectedValues, selectedAttribute],
    );

    const errorMessage = error instanceof Error ? error.message : error;

    const threediumModel: IThreediumModel | null = useMemo(() => {
        if (product3dModel && !brokenModels[ProductModelVisualizerKey]) {
            return product3dModel;
        }

        if (!selectedVisualizerAttribute) {
            return null;
        }

        return brokenModels[selectedVisualizer?._id] ? null : selectedVisualizer?.threedium;
    }, [product3dModel, selectedVisualizer, selectedVisualizerAttribute, brokenModels]);

    const planar2dModel = product2dModel ?? selectedVisualizer?.planar2d;

    const threediumCustomizations: IThreediumCustomization[] = useMemo(() => {
        if (!attributesToShow?.length || !visualizationDependencies) {
            return [];
        }

        return attributesToShow
            .map((attribute) => {
                const id = attribute._id;
                const selectedValue = selectedValues?.get(id);
                const nestedValues: TNestedCustomizationSelection[] | undefined = selectedValue?.nestedValues;
                const overrideAttribute = nestedValues?.find((a) => a.target);
                const nestedAttributeId = overrideAttribute?.target?.destinationId;
                let selectedNestedAttributeId;

                if (nestedAttributeId) {
                    Object.keys(visualizationDependencies).forEach((attributeId) => {
                        if (visualizationDependencies[attributeId].visualizerAttribute?._id === nestedAttributeId) {
                            // this will override attribute with a picked nested value when exists
                            selectedNestedAttributeId = attributeId;
                        }
                    });
                }

                const { visualizer, visualizerAttribute, binding } =
                    visualizationDependencies?.[selectedNestedAttributeId ?? id] ?? {};

                return {
                    model: threediumModel ?? visualizer?.threedium,
                    planarData: visualizer?.planar2d,
                    visualizerAttribute,
                    threedium: visualizerAttribute?.threedium,
                    planar2d: visualizerAttribute?.planar2d,
                    binding: overrideAttribute?.target ?? binding, //needed for rotation, picking deep one first
                    selection: selectedValue,
                    defaults: defaultValues?.get(id),
                    attribute,
                    visualizer,
                };
            })
            .filter(({ model }: IThreediumCustomization) => {
                const keys: (keyof IThreediumModel)[] = ["distId", "solutionId"];

                return keys.every((key) => model?.[key] === threediumModel?.[key]);
            });
    }, [attributesToShow, defaultValues, visualizationDependencies, selectedValues, threediumModel]);

    const templatesMap = useMemo(
        () =>
            visualizers?.reduce((map, visualizer) => {
                const model = visualizer?.threedium;
                if (model) {
                    map[visualizer._id] = {
                        ...model,
                        distID: model.distId,
                        solution3DName: model.solutionName,
                        solution3DID: model.solutionId,
                    };
                }

                return map;
            }, {}),
        [visualizers],
    );

    const handleLoadError = useCallback(
        (err, visualizerId) => {
            if (brokenModels[visualizerId]) {
                return;
            }

            setBrokenModels((brokenModels) => ({
                ...brokenModels,
                [visualizerId]: true,
            }));
        },
        [brokenModels],
    );

    const customizers = useMemo(() => {
        const customizer3dBlock = (
            selected: boolean,
            visualizerId: string,
            template?: IThreediumTemplate,
            productModel?: IThreediumModel,
        ) => (
            <div className={classNames(classes.preview3d, { selected })} key={visualizerId}>
                <Customizer3d
                    productModel={productModel}
                    template={template}
                    attribute={threediumModelAttribute}
                    customizations={threediumCustomizations}
                    onError={handleLoadError}
                    visualizerId={visualizerId}
                />
            </div>
        );

        const customizer2dBlock = (visualizerId: string, selectedVisualizerId: string) => (
            <Customizer2d
                key={visualizerId}
                customizations={threediumCustomizations}
                selectedVisualizerId={selectedVisualizerId}
                productModel={product2dModel}
            />
        );

        const productModelCustomizeBlock =
            product3dModel && !brokenModels[ProductModelVisualizerKey]
                ? [customizer3dBlock(true, ProductModelVisualizerKey, undefined, product3dModel)]
                : [];

        //First go product level model, with all the rest as a fallback
        return productModelCustomizeBlock.concat(
            _.map(visualizers, (visualizer) => {
                if (!activeVisualizers.has(visualizer._id)) {
                    return null;
                }

                if (visualizer.threedium) {
                    return customizer3dBlock(
                        threediumModel === visualizer.threedium,
                        visualizer._id,
                        templatesMap[visualizer._id],
                    );
                }

                return !visualizer.planar2d ? null : customizer2dBlock(visualizer._id, selectedBinding?.destinationId);
            }),
        );
    }, [
        visualizers,
        activeVisualizers,
        product3dModel,
        product2dModel,
        templatesMap,
        threediumModel,
        threediumModelAttribute,
        threediumCustomizations,
        brokenModels,
        classes.preview3d,
        handleLoadError,
        selectedBinding?.destinationId,
    ]);

    const attributeImageUrl = useMemo(
        () =>
            [selectedAttribute, customizationTemplate, selectedVisualizer].reduce(
                (result: string | undefined, value) => result ?? value?.image?.url,
                undefined,
            ),
        [selectedAttribute, selectedVisualizer, customizationTemplate],
    );

    const handleDropdownChange = useCallback(
        (selectedAttributes: IAttribute[]) => {
            setSelectedOptionalAttributes(mutateAttributes(selectedAttributes));

            if (!defaultAttributesValues) {
                return;
            }

            setSelectedValues((selectedValues) => {
                const newAttributes = requiredAttributes.concat(selectedAttributes);

                return newAttributes.reduce((result, attribute) => {
                    const { _id: id } = attribute;
                    const value = selectedValues.get(id) ?? defaultAttributesValues?.get(id);
                    if (value) {
                        result.set(id, value);
                    }

                    return result;
                }, new CustomizationSelectionMap());
            });
        },
        [requiredAttributes, defaultAttributesValues],
    );

    const handleDeleteAttribute = useCallback(
        (id: string) => {
            const values = new CustomizationSelectionMap(selectedValues);
            values.delete(id);
            setSelectedValues(values);

            setAttributeValidationError(values);
            setAttributesToShow(deleteAttributeById(id));
            setSelectedOptionalAttributes(deleteAttributeById(id));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedValues],
    );

    const productCustomizationContent = () => {
        return (
            <div
                data-test={CUSTOMIZATIONS.CUSTOMIZE_MODAL}
                id="dialogContent"
                className={classNames(classes.modalContainer, "modalContainer")}
            >
                <div className={classes.modalHeader}>
                    <div className={classes.modalTitle}>
                        {formatMessage("Customize")}

                        <span className={classes.productName}>{" - "}</span>

                        <span data-test={CUSTOMIZATIONS.CUSTOMIZE_MODAL_TITLE} className={classes.productName}>
                            {productName}
                        </span>
                    </div>
                    {invalidRequiredAttributes.size > 0 && (
                        <div
                            data-test={CUSTOMIZATIONS.REQUIRED_ATTRIBUTE_ERROR}
                            className={classes.requiredAttributeError}
                        >
                            {formatMessage("Missing customization requirement(s).")}
                        </div>
                    )}
                </div>

                {errorMessage && <div className={classes.errorMessage}>{errorMessage}</div>}

                {!errorMessage && (
                    <div className={classes.contentWrapper}>
                        <div className={classes.previewContainer}>
                            {customizers}

                            {!threediumModel && !planar2dModel && (
                                <CustomizationImageView attributeImageUrl={attributeImageUrl} product={product} />
                            )}
                        </div>
                        <div className={classes.attributesListContainer}>
                            <div className={classes.attributesList}>
                                <div className={classes.productInfo}>
                                    <div className={classes.productTitle}>
                                        <p className={classes.productInfoName}>{productName}</p>
                                        {optionalAttributeLimit && !readonly && (
                                            <div className={classes.dropdownWrapper}>
                                                <AttributesDropdown
                                                    attributeLimit={optionalAttributeLimit}
                                                    attributes={optionalAttributes}
                                                    onChange={handleDropdownChange}
                                                    onValidationError={handleAttributeValidationError}
                                                    attributesAmountValidationError={
                                                        validationErrors.get(ATTRIBUTES_DROPDOWN_KEY) ?? null
                                                    }
                                                    selectedAttributes={selectedOptionalAttributes}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {templateDescription && (
                                        <div data-test={CUSTOMIZATIONS.PRODUCT_DESCRIPTION}>
                                            <CustomizationDescription className={classes.productDescription}>
                                                {templateDescription}
                                            </CustomizationDescription>
                                        </div>
                                    )}
                                </div>
                                <div data-test={`${CUSTOMIZATIONS.CUSTOMIZATION_MODAL_ATTRIBUTE_PICKERS_LIST}`}>
                                    {attributesToShow?.map((attribute, index) => {
                                        const { _id: id, image } = attribute ?? {};
                                        const imageUrl = image?.url ?? getVisualizerImage(attribute, visualizers ?? []);

                                        const isSelected = selectedAttribute?._id === id;
                                        const isOpened = isSelected && showAttribute;
                                        return !selectedValues.has(id) ? null : (
                                            <div
                                                className={classNames(classes.attribute, {
                                                    active: isSelected,
                                                })}
                                                data-test={`${CUSTOMIZATIONS.CUSTOMIZATION_MODAL_ATTRIBUTE_WRAPPER}_${index}`}
                                                key={id}
                                            >
                                                <Attribute
                                                    attribute={attribute}
                                                    isOpened={isOpened}
                                                    isSelected={isSelected}
                                                    hasError={
                                                        !!validationErrors.get(id) || invalidRequiredAttributes.has(id)
                                                    }
                                                    isDeletable={!readonly && !!optionalAttributeLimit}
                                                    imageUrl={imageUrl}
                                                    onDelete={handleDeleteAttribute}
                                                    data-key={index}
                                                    data-attribute-id={`${id}`}
                                                    data-test={`${CUSTOMIZATIONS.CUSTOMIZATION_MODAL_ATTRIBUTE}_${index}`}
                                                    onClick={handleAttributeClick}
                                                />

                                                <Collapse
                                                    in={isOpened}
                                                    data-test={`${CUSTOMIZATIONS.ATTRIBUTE_DETAILS_COLLAPSE}`}
                                                >
                                                    {!isSelected ? null : (
                                                        <div
                                                            className={classNames(classes.attributeDetailsContainer, {
                                                                isOpened,
                                                            })}
                                                            data-test={`${CUSTOMIZATIONS.ATTRIBUTE_DETAILS_CONTAINER}`}
                                                        >
                                                            <AttributeDetails
                                                                attribute={selectedAttribute}
                                                                product={product}
                                                                onChange={handleAttributeChange}
                                                                onValidationError={handleAttributeValidationError}
                                                                selectedValue={selectedValue}
                                                                readonly={readonly}
                                                                companyMerchantId={companyMerchantId}
                                                            />
                                                        </div>
                                                    )}
                                                </Collapse>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Dialog
                modal={false}
                open={open}
                onRequestClose={handleClose}
                autoScrollBodyContent={false}
                autoDetectWindowHeight={true}
                repositionOnUpdate={false}
                contentStyle={styles.content}
                bodyStyle={styles.body}
                actionsContainerStyle={styles.actions}
                fixIOS={true}
                dataTest={{ closeButton: CUSTOMIZATIONS.CLOSE_CUSTOMIZE_MODAL_BUTTON }}
                actions={
                    readonly ? (
                        <Button
                            data-test={CUSTOMIZATIONS.CUSTOMIZE_MODAL_CANCEL_BTN}
                            label={formatMessage("Close")}
                            style={styles.button}
                            onClick={handleClose}
                            key="close"
                        />
                    ) : (
                        [
                            <Button
                                data-test={CUSTOMIZATIONS.CUSTOMIZE_MODAL_CANCEL_BTN}
                                label={formatMessage("Cancel")}
                                style={styles.button}
                                onClick={handleClose}
                                key="close"
                            />,
                            ...(!(isPDP && allowBulkApplyCustomizations)
                                ? []
                                : [
                                      <Button
                                          data-test={CUSTOMIZATIONS.CUSTOMIZE_MODAL_BULK_CUSTOMIZATIONS_BTN}
                                          disabled={hasValidationErrors}
                                          label={formatMessage("Save and Apply Customization to more products")}
                                          style={styles.button}
                                          onClick={handleOpenBulkModal}
                                          key="bulkCustomizations"
                                      />,
                                  ]),
                            <Button
                                data-test={CUSTOMIZATIONS.CUSTOMIZE_MODAL_SAVE_BTN}
                                disabled={hasValidationErrors}
                                primary={true}
                                label={formatMessage("Save")}
                                onClick={handleSave}
                                key="complete"
                            />,
                        ]
                    )
                }
            >
                <CustomizationModalProvider>
                    {!isFetching || !isProductFetching ? (
                        productCustomizationContent()
                    ) : (
                        <div className={classes.circularProgressContainer}>
                            <CircularProgress size={50} thickness={4} style={styles.circularProgress} />
                        </div>
                    )}
                </CustomizationModalProvider>
            </Dialog>
            <ConfirmDialog
                title={formatMessage("Are you sure? All unsaved changes will be lost")}
                open={dirtyDialogOpen}
                onConfirm={handleForceClose}
                onCancel={handleDirtyDialogClose}
                confirmLabel={formatMessage("Leave")}
            />
        </>
    );
};

const styles = {
    content: {
        maxWidth: "1400px",
        width: "90vw",
        height: "80vh",
        maxHeight: "80vh",
        transform: "none",
    },
    body: {
        height: "100%",
        padding: "2vh 2vw 0 2vw",
    },
    circularProgress: {
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        width: "94px",
        height: "100%",
        margin: "auto",
    },
    actions: {
        padding: "2vh 3vh",
    },
    button: {
        height: "4vh",
        lineHeight: "4vh",
        marginRight: "16px",
    },
} as const;

const useStyles = makeStyles({
    modalContainer: {
        display: "flex",
        flexDirection: "column",
    },
    modalHeader: {
        lineHeight: "4vh",
        height: "6vh",
        padding: "10px",
        textAlign: "left",
        verticalAlign: "middle",
        color: "black",
        borderBottom: `1px solid ${theme.palette.borderColor}`,
        display: "flex",
        marginBottom: "2vh",
        justifyContent: "space-between",
        alignItems: "center",
    },
    modalTitle: {
        fontSize: 16,
        fontWeight: 700,
        color: theme.palette.textColor,
        maxWidth: "70%",
    },
    productName: {
        fontSize: 14,
        fontWeight: 400,
        color: theme.palette.darkGrey,
    },
    contentWrapper: {
        display: "flex",
        flexDirection: "row",
        alignItems: "start",
        gap: 26,
        height: "65vh",
        maxHeight: "65vh",
    },
    previewContainer: {
        flex: "0 0 400px",
        height: "400px",
        display: "flex",
        justifyContent: "stretch",
        alignItems: "stretch",
        backgroundColor: "#f6f6f6",
        ["@media (min-width:992px)"]: {
            flex: "0 0 580px",
            height: "580px",
        },
    },
    preview3d: {
        flex: 1,
        width: "100%",
        minHeight: "400px",
        display: "none",
        "&.selected": {
            display: "block",
        },
    },
    attributesListContainer: {
        width: "50%",
        flex: "1",
        height: "95%",
        overflowY: "hidden",
    },
    attributesList: {
        flex: "1 1 auto",
        height: "100%",
        overflowY: "auto",
        scrollbarColor: `auto ${theme.palette.white}`,
        scrollbarGutter: "stable",
    },
    productInfo: {
        marginBlockEnd: 16,
        display: "flex",
        flexDirection: "column",
    },
    productTitle: {
        display: "flex",
        flexDirection: "row",
    },
    productDescription: {
        marginTop: "10px",
        color: theme.palette.black,
    },
    productInfoName: {
        fontSize: 20,
        color: theme.palette.textColor,
    },
    attribute: {
        fontSize: "20px",
        borderTop: `1px solid ${theme.palette.borderColor}`,
    },
    attributeButtonIcon: {
        height: 24,
        marginInlineStart: "auto",
        fill: theme.palette.darkGrey,
    },
    attributeDetailsContainer: {
        flex: "1",
        ".isOpened": {
            display: "block",
        },
    },
    attributeDetailsTitle: {
        padding: "20px",
        fontSize: "20px",
        color: `${theme.palette.textColor}`,
    },
    circularProgressContainer: {
        height: "100%",
        width: "100%",
    },
    errorMessage: {
        padding: "40px 10px",
        fontSize: 14,
        color: theme.palette.error,
        wordBreak: "break-word",
    },
    attributeError: {
        border: "1px solid #f5beb8",
        "& .MuiAlert-message": {
            fontSize: 12,
            color: "#d86c61",
        },
    },
    dropdownWrapper: {
        marginLeft: "auto",
    },
    requiredAttributeError: {
        width: "54%",
        color: theme.palette.errorColor,
    },
});

const mapStateToProps = (state: IStore): ICustomizationModalStateProps => {
    return {
        scope: state.scope,
        error: state.customization.current?.error,
        isFetching: state.customization.current?.isFetching,
    };
};

const mapDispatchToProps = (dispatch): ICustomizationModalDispatchProps => ({
    resetUploadedImage: () => dispatch(resetUploadedImage()),
});

export const ProductCustomizationModal = connect<ICustomizationModalStateProps, {}, ICustomizationModalProps>(
    mapStateToProps,
    mapDispatchToProps,
)(CustomizationModalContainer);
